import * as React from 'react';

import { Header } from '@tanstack/react-table';
import { GripVertical } from 'lucide-react';

import { cn } from '@/lib/utils';

interface DataTableResizeHandleProps<TData> {
  header: Header<TData, unknown>;
  onResizeEnd?: () => void;
}

export function DataTableResizeHandle<TData>({
  header,
  onResizeEnd,
}: DataTableResizeHandleProps<TData>) {
  const isResizing = header.column.getIsResizing();
  const wasResizingRef = React.useRef(false);
  const onResizeEndRef = React.useRef(onResizeEnd);
  onResizeEndRef.current = onResizeEnd;

  // Track when resizing stops and trigger save
  React.useEffect(() => {
    if (wasResizingRef.current && !isResizing) {
      onResizeEndRef.current?.();
    }
    wasResizingRef.current = isResizing;
  }, [isResizing]);

  if (!header.column.getCanResize()) {
    return null;
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      tabIndex={0}
      onMouseDown={header.getResizeHandler()}
      onTouchStart={header.getResizeHandler()}
      onKeyDown={e => {
        // Allow keyboard navigation - prevent default to avoid conflicts
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
        }
      }}
      onClick={e => e.stopPropagation()} // Prevent sorting when clicking resize handle
      className={cn(
        'absolute right-0 top-0 flex h-full w-4 cursor-col-resize items-center justify-center',
        'opacity-0 transition-opacity hover:opacity-100',
        'group-hover/header:opacity-50',
        isResizing && 'bg-primary/10 opacity-100'
      )}
    >
      <GripVertical className="h-4 w-3 text-muted-foreground" />
    </div>
  );
}
